import type { FileResponse } from "@efes/shared";
import { useEffect, useId, useRef } from "react";

interface DeleteConfirmDialogProps {
	file: FileResponse;
	/** Другие записи files с тем же filePath (не включая текущую) */
	siblingCount: number;
	onConfirm: () => void;
	onCancel: () => void;
}

const cascadeWarnings: Record<string, string> = {
	rate_book: "Связанные расценки будут удалены.",
	work_order: "Связанные строки наряда будут удалены.",
	completion_act: "Связанные строки акта будут удалены.",
};

/**
 * Модальный диалог подтверждения удаления записи файла/листа.
 *
 * В мульти-листовом режиме одна Excel-книга порождает несколько записей `files`
 * с общим `filePath`. Физический файл удаляется ref-counting'ом только когда
 * последняя запись с этим `filePath` удалена (см. `deleteFileWithCascade`).
 *
 * Диалог показывает три блока информации:
 * 1. Что именно удаляется (лист или файл) — заголовок и тело.
 * 2. Что произойдёт с физическим файлом на диске.
 * 3. Что удалится каскадно (строки наряда/акта/расценки).
 */
export function DeleteConfirmDialog({
	file,
	siblingCount,
	onConfirm,
	onCancel,
}: DeleteConfirmDialogProps) {
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	const hasSheetName = typeof file.sheetName === "string" && file.sheetName.trim() !== "";
	const title = hasSheetName ? `Удалить лист «${file.sheetName}»?` : "Удалить файл?";

	let physicalFileNote: string;
	if (siblingCount > 0) {
		physicalFileNote = `Физический файл останется — используется ещё в ${siblingCount} ${pluralizeRecord(siblingCount)}.`;
	} else if (hasSheetName) {
		physicalFileNote =
			"Это последний лист этого файла — физический файл также будет удалён с диска.";
	} else {
		physicalFileNote = "Физический файл будет удалён с диска.";
	}

	const cascadeNote = cascadeWarnings[file.fileType];

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="mb-1 text-lg font-semibold text-slate-900">
				{title}
			</h3>

			<div className="mb-4 space-y-2 text-sm text-slate-600">
				<p>
					{hasSheetName ? (
						<>
							Лист <span className="font-medium text-slate-900">«{file.sheetName}»</span> файла{" "}
							<span className="text-slate-700">«{file.originalName}»</span> будет удалён.
						</>
					) : (
						<>
							Файл <span className="font-medium text-slate-900">«{file.originalName}»</span> будет
							удалён.
						</>
					)}
				</p>
				<p className="text-amber-700">{physicalFileNote}</p>
				{cascadeNote && <p className="text-amber-700">{cascadeNote}</p>}
			</div>

			<div className="flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
				>
					Отмена
				</button>
				<button
					type="button"
					onClick={onConfirm}
					className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
				>
					Удалить
				</button>
			</div>
		</dialog>
	);
}

/** Склонение слова «запись» для числительных (1, 2–4, 5+) */
function pluralizeRecord(n: number): string {
	const mod10 = n % 10;
	const mod100 = n % 100;
	if (mod10 === 1 && mod100 !== 11) return "записи";
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "записях";
	return "записях";
}
